import first from 'lodash/first.js';
import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';

import { handleResponseError } from '@/Helpers.js';
import Remediation from '@/models/Remediation.js';
import axios from '@/plugins/axios.js';
import { gate } from '@/plugins/vue-gate/Index.js';
import store from '@/store/Index.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

/**
 * Handles navigation guards for quiz and survey attempts.
 *
 * @param {Object} to The target Route Object being navigated to.
 * @param {Object} from The current Route Object being navigated from.
 * @param {Function} next This function must be called to resolve the hook.
 */
export default async (to, from, next) => {
    // Directly proceed if navigating to '403' page
    if (to.name === '403') return next();

    try {
        const { user } = store.state.Auth;

        // Check if user is not logged in or does not have permission to view any quiz attempts
        if (!user || gate.denies('viewAny', 'QuizAttempt')) {
            return next();
        }

        // Determine if user has an active quiz and if the current attempt is already hydrated
        const userHasQuizActiveId = store.getters['User/hasActiveQuiz'];
        const userHasQuizData = !!store.getters['User/get']('quiz_id') && !!store.getters['User/get']('quiz_activity');
        const hasHydratedAttempt = store.getters['QuizAttempt/has'](to.params?.id || 0);

        // Skip further checks if user has an active quiz and attempt is hydrated
        if (userHasQuizActiveId && hasHydratedAttempt) {
            return next();
        }

        // Build query to fetch active quiz attempts
        const inProgressQuery = new QueryBuilder('quiz-attempts')
            .filter('active_attempt', 1)
            .include('automatedSurveyAssignment.survey');

        // Fetch the first active attempt, if any
        const activeAttempt = first(
            await axios.get(inProgressQuery.toUrl(), { errorHandle: false }).then(resp => resp.data.data)
        );

        // Determine if there's a lockdown condition in the active attempt
        const lockdown = activeAttempt
            ? activeAttempt.quiz_activity?.lockdown ||
              activeAttempt.survey_activity?.lockdown ||
              activeAttempt.automated_survey_assignment?.is_locked_now
            : null;

        // If the active attempt is linked to an automated survey assignment, proceed
        if (activeAttempt?.automated_survey_assignment) {
            return next();
        }

        // Handle quiz or survey lockdown situations
        if (activeAttempt) {
            // Determine the type of the active attempt
            const typeKey =
                activeAttempt.survey_activity_id || activeAttempt.automated_survey_assignment ? 'survey' : 'quiz';

            // Select the appropriate activity based on the type
            const activity = activeAttempt.quiz_activity_id
                ? activeAttempt.quiz_activity
                : activeAttempt.survey_activity;

            // Extract the quiz version ID
            const quizId = activeAttempt[`${typeKey}_activity`].quiz_version_id;

            // If the attempt is not hydrated, no quiz data present, and in lockdown
            if (!hasHydratedAttempt && !userHasQuizData && lockdown) {
                const { quiz } = activeAttempt;

                // Attach the quiz activity to the quiz object
                quiz.quiz_activity = activeAttempt[`${typeKey}_activity`];

                // Build a query to fetch remediation data
                const remediationQuery = new QueryBuilder('remediation')
                    .filter('course_id', activity.activity.course_id)
                    .filter('activity_id', activity.activity.id)
                    .filter('user_id', user.id);

                // Fetch and process the remediation data
                await Remediation.api().get(remediationQuery.toUrl());
                const remediation = Remediation.query()
                    .where('course_id', activity.activity.course_id)
                    .where('activity_id', activity.activity.id)
                    .where('user_id', user.id)
                    .first();

                // Apply time limit from remediation if applicable
                if (remediation && Number(get(remediation, 'quiz_time_limit')) > 0) {
                    quiz.quiz_activity.time_limit = remediation.quiz_time_limit;
                }

                // Update the store with the new quiz attempt data
                await store.dispatch('QuizAttempt/add', { ...quiz });
                await store.commit('User/SET', { field: 'quiz_id', value: quizId });
                await store.commit('User/SET', { field: 'quiz_activity', value: activity });
                await store.commit('User/SET', { field: 'quiz_active_id', value: activeAttempt.id });

                // Hydrate the quiz attempt in the store
                await store.dispatch(`QuizAttempt/${quiz.id}/hydrateQuizAttempt`, { quiz_attempt: activeAttempt });
            }

            // Redirect if not on an attempt page and in lockdown
            if (!to.name?.includes('attempt') && lockdown) {
                // Handle redirection for automated survey assignments
                if (activeAttempt.automated_survey_assignment) {
                    return next({
                        name: 'automated-survey-attempt',
                        params: {
                            id: activeAttempt.automated_survey_assignment.survey_id,
                            assignmentId: activeAttempt.automated_survey_assignment.id,
                            attemptId: activeAttempt.id,
                        },
                        replace: true,
                    });
                }

                // Redirect to the appropriate attempt page based on the type
                return next({
                    name: `${typeKey}-attempt`,
                    params: {
                        activityId: activity.activity.id,
                        courseId: activity.activity.course_id,
                        id: quizId,
                        quizActivityId: activeAttempt[`${typeKey}_activity_id`],
                        quizVersionId: quizId,
                    },
                    replace: true,
                });
            }

            // In a scenario where the user is hard refreshing on the quiz or survey attempt page
            // Vue Router will lose the route name and important parameters, below we attempt
            // to add the missing parameters back to the route object
            if (!to.name && from.path === '/' && (to.path.includes('quiz') || to.path.includes('survey'))) {
                const updatedRoute = {
                    ...to,
                    name: `${typeKey}-attempt`,
                    params: {
                        ...to.params,
                        attemptId: activeAttempt.id,
                        quizVersionId: quizId,
                        quizActivityId: activeAttempt[`${typeKey}_activity_id`],
                    },
                };

                return next(updatedRoute);
            }
        }

        // Handle navigation for cases where there's no active attempt but navigation is attempted to a quiz or survey
        const checklist = ['quiz-attempt', 'survey-attempt'];

        // Check if coming from a hard refresh, typically the name and redirect properties
        // of both the to and from objects will be undefined
        const isHardRefresh = !to.name && !to.redirect && !from.name && !from.redirect;

        // Redirect the student if there is no active attempt, and the target route is a quiz or survey.
        // In the event where the navigation event is form a hard refresh, redirect to the activity view page as well.
        // This is to prevent an edge case where the student is redirected to the attempt page after ending an attempt.
        if (isEmpty(activeAttempt) && (checklist.some(item => to.name === item) || isHardRefresh)) {
            return next({
                name: 'activity-view',
                params: { ...to.params },
            });
        }

        // Proceed with the original navigation if none of the above conditions are met
        return next();
    } catch (error) {
        handleResponseError(error);
    }

    // Fallback next call to ensure navigation resolution
    return next();
};
